import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { useLazyQuery } from '@apollo/client';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ContentCopy } from '@mui/icons-material';
import { Alert, Box, Button, Collapse, Typography } from '@mui/material';
import { useState, type FC } from 'react';
import GET_DGA_CONFIGURATION from '../../graphql/queries/getDGAConfiguration';

type DGAConfigValues = {
  rut: string;
  password: string;
};

type Props = {
  onCopy: (config: DGAConfigValues) => void;
};

const CopyDGAConfigSection: FC<Props> = ({ onCopy }) => {
  const { _ } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const [sourceDeviceId, setSourceDeviceId] = useState<string | null>(null);

  const [fetchConfig, { data, loading, error }] = useLazyQuery(GET_DGA_CONFIGURATION);

  const handleDeviceChange = (_: unknown, value: string | null) => {
    setSourceDeviceId(value);
    if (value) {
      fetchConfig({ variables: { deviceId: value } });
    }
  };

  // Solo usar la config si el dato cargado corresponde al dispositivo seleccionado.
  // useLazyQuery conserva el `data` anterior mientras refetcha, por lo que sin esta
  // correlación se podría copiar la configuración del dispositivo equivocado.
  const config =
    data?.device?.id === sourceDeviceId ? data?.device?.dga?.configuration : undefined;

  // TODO(AMARU-447): auditar la lectura de credenciales DGA al copiar desde otro
  // dispositivo. Hoy se reusa GET_DGA_CONFIGURATION (lectura sin traza). Reemplazar por
  // una query/mutation dedicada `copyDgaConfigurationSource` con @Auth.role('ADMIN') que
  // escriba un audit log (responsible=user, action=COPY_DGA_CONFIGURATION, entity=device).
  const handleCopy = () => {
    if (!config) return;
    // Solo se copian rut y password: son los atributos replicables entre dispositivos
    // del mismo titular. `code` (punto de captación) y `type` son caso a caso.
    onCopy({
      rut: config.rut ?? '',
      password: config.password ?? '',
    });
    setExpanded(false);
    setSourceDeviceId(null);
  };

  const hasDGAConfig = !!config;
  const canCopy = !!sourceDeviceId && hasDGAConfig && !loading;

  return (
    <Box mt={1}>
      <Button
        size="small"
        startIcon={<ContentCopy fontSize="small" />}
        onClick={() => setExpanded((prev) => !prev)}
        variant="text"
        color="secondary"
      >
        <Trans>Copiar desde otro dispositivo</Trans>
      </Button>
      <Collapse in={expanded}>
        <Box mt={1} p={2} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            <Trans>
              Selecciona el dispositivo del que quieres copiar la configuración DGA. Los campos se
              llenarán con los datos del dispositivo seleccionado.
            </Trans>
          </Typography>
          <StandardAutocompleteIdsWithFallback
            dataType="devices"
            label={_(msg`Dispositivo origen`)}
            value={sourceDeviceId}
            onChange={handleDeviceChange}
          />
          {sourceDeviceId && !loading && !hasDGAConfig && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              <Trans>El dispositivo seleccionado no tiene configuración DGA</Trans>
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error.message}
            </Alert>
          )}
          <Button
            disabled={!canCopy}
            onClick={handleCopy}
            variant="contained"
            size="small"
            sx={{ mt: 1.5 }}
          >
            <Trans>Copiar configuración</Trans>
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CopyDGAConfigSection;
